import { useEffect, useLayoutEffect } from "react";

/*
 * The pre-React HTML splash (#initial-loader, in index.html) comes down once
 * an InitialLoaderHider has mounted and no KeepInitialLoader is mounted.
 *
 * App mounts the hider inside its route <Suspense>, so a route whose chunk is
 * still downloading keeps it unmounted. PublicLayout has a boundary of its own
 * around the page, and that boundary catches the page's suspension first. So
 * App's boundary commits (header, empty <main>, footer) and the hider mounts
 * while the page is still on its way. PublicLayout's boundary therefore shows
 * a KeepInitialLoader as its fallback, which holds the splash until the page
 * replaces it. Without the hold the splash went before the page existed, and
 * the footer jumped when the page arrived: CLS 0.51–0.88 on every public page
 * (section 3.3 of docs/plans/public-pages-performance-2026-10-16.md).
 *
 * The layout renders under the splash rather than waiting for the page, so
 * that its work overlaps the chunk's download instead of delaying the page's
 * first request (measured at 140–260 ms on a phone when it did not).
 */

// Start fading the moment the route is on screen. This used to hold the
// loader for a further 2s, which meant pages whose data had settled at ~3s
// stayed hidden behind the splash until ~5.7s — the loader outlived the
// content it was covering. FADE_MS matches the CSS transition in index.html;
// the extra 100ms is slack before the node is removed.
const FADE_MS = 200;

interface SplashState {
  requested: boolean;
  holds: number;
}

// Kept per splash element, so the state goes with the node it governs.
const states = new WeakMap<HTMLElement, SplashState>();

function currentSplash() {
  const element = document.getElementById("initial-loader");
  if (!element) return null;
  let state = states.get(element);
  if (!state) {
    state = { requested: false, holds: 0 };
    states.set(element, state);
  }
  return { element, state };
}

function hideWhenFree(splash: { element: HTMLElement; state: SplashState }) {
  // A microtask later, not now. StrictMode (development) disconnects and
  // reconnects every layout effect straight after mounting, and the hold it
  // drops for that moment must not count as a release. No animation frame
  // either: a background tab runs none, and the splash would stay up there.
  queueMicrotask(() => {
    const { element, state } = splash;
    if (!state.requested || state.holds > 0) return;
    if (element.classList.contains("loaded")) return;
    element.classList.add("loaded");
    setTimeout(() => {
      element.remove();
    }, FADE_MS + 100);
  });
}

/**
 * Takes the splash down once nothing holds it. Mount this inside <Suspense>,
 * so that suspension keeps it unmounted until the route has rendered.
 *
 * Its request outlives it on purpose: if the tree under it is torn down (a page
 * that throws lands on AuthErrorBoundary), the hold released by that teardown
 * must still find the request there.
 */
export function InitialLoaderHider() {
  useEffect(() => {
    const splash = currentSplash();
    if (!splash) return;
    splash.state.requested = true;
    hideWhenFree(splash);
  }, []);

  return null;
}

/**
 * Holds the splash while it is mounted: the fallback for a boundary that
 * commits before its page exists. A layout effect, so the hold is in place
 * before any InitialLoaderHider in the same commit runs its effect.
 */
export function KeepInitialLoader() {
  useLayoutEffect(() => {
    const splash = currentSplash();
    if (!splash) return;
    splash.state.holds += 1;
    return () => {
      splash.state.holds -= 1;
      hideWhenFree(splash);
    };
  }, []);

  return null;
}
